/**
 * Utility functions for time slot operations
 */

import { minutesToTimeString, formatTime } from '@butter/library/datetime';
import { isStartableRow } from '@butter/library/availability';
import { formatHoursDisplay } from '@butter/library/shared';
import type { TimeSegmentConfig, TimeSlot, SelectionRange } from './types';

/**
 * Time segment boundaries (in minutes from midnight)
 * Early Morning: 0-449 (12:00 AM - 7:29 AM)
 * Morning: 450-719 (7:30 AM - 11:59 AM)
 * Afternoon: 720-1079 (12:00 PM - 5:59 PM)
 * Evening: 1080-1439 (6:00 PM - 11:59 PM)
 */
export const TIME_SEGMENTS: TimeSegmentConfig[] = [
  { id: 'early-morning', label: 'Early Morning', startMinutes: 0, endMinutes: 449 },
  { id: 'morning', label: 'Morning', startMinutes: 450, endMinutes: 719 },
  { id: 'afternoon', label: 'Afternoon', startMinutes: 720, endMinutes: 1079 },
  { id: 'evening', label: 'Evening', startMinutes: 1080, endMinutes: 1439 },
];

/**
 * Convert minutes from midnight to formatted time string
 * Uses @butter/library/datetime for consistent time formatting across the app
 * 
 * @param minutes - Minutes from midnight (0-1439)
 * @returns Formatted time string (e.g., "7:30 AM", "12:00 PM", "9:45 PM")
 */
export function formatMinutesToTime(minutes: number): string {
  // Convert minutes to HH:MM format (24-hour)
  const timeIn24Hour = minutesToTimeString(minutes);
  // Convert to 12-hour format with AM/PM
  return formatTime(timeIn24Hour, false);
}

/**
 * Format a selection's end time, normalizing a midnight crossing first.
 *
 * `minutesToTimeString` THROWS above 1440, but a club may legally reach 1560
 * (2 AM) — so callers formatting a booked end must normalize before it, not
 * after.
 */
export function formatSlotEndTime(endMinutes: number): string {
  // Fold, don't subtract once — `docs/conventions/slot-times.md` ("display normalizes
  // the sentinel; it never renders it raw"). Same answer for every reachable value, and
  // it stops being a partial function if a caller ever hands it something past 2880.
  return formatMinutesToTime(endMinutes % 1440);
}

/**
 * ⚠️ NO CALLERS — verified across `apps/` and `apps/e2e`. The live grouping is an inline
 * filter in `TimeSlotSegment.tsx`, which uses `.filter` with no fallback bucket, so a slot
 * matching no segment is DROPPED rather than defaulted here. Two reviews in a row proposed
 * fixing this function for past-midnight slots; fixing it would change nothing.
 * Delete it or wire it up — see the past-midnight client ticket.
 *
 * Get the segment that a time slot belongs to
 * @param startMinutes - Start time in minutes from midnight
 * @returns The segment configuration
 */
export function getSegmentForTime(startMinutes: number): TimeSegmentConfig {
  return (
    TIME_SEGMENTS.find(
      (seg) => startMinutes >= seg.startMinutes && startMinutes <= seg.endMinutes
    ) || TIME_SEGMENTS[1] // Default to morning
  );
}

/**
 * Format duration in minutes: sub-hour is "{mins} min", an hour or more is
 * decimal hours with a trailing ".0" trimmed — 90 -> "1.5 hr", 120 -> "2 hr".
 * @param minutes - Duration in minutes
 * @returns Formatted string (e.g., "30 min", "1 hr", "1.5 hr")
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }

  return formatHoursDisplay(minutes, { decimals: 2, unit: ' hr' });
}

/**
 * Check if a time slot is available for booking based on membership status
 * 
 * Two-step validation:
 * 1. Is the slot available? (API sets isAvailable=false for booked/past/blocked slots)
 * 2. Does the user have permission? (membership-based access control)
 * 
 * @param slot - The time slot to check
 * @param isMember - Whether the user is a member
 * @returns True if the slot can be booked by this user
 * 
 * @example
 * // Slot is booked (API sets isAvailable=false)
 * isSlotAvailable({ isAvailable: false, canBookPublic: true, canBookMember: true }, false);
 * // Returns: false (slot is not available regardless of permissions)
 * 
 * @example
 * // Slot is available but members-only
 * isSlotAvailable({ isAvailable: true, canBookPublic: false, canBookMember: true }, false);
 * // Returns: false (public user doesn't have permission)
 * 
 * @example
 * // Slot is available and public can book
 * isSlotAvailable({ isAvailable: true, canBookPublic: true, canBookMember: true }, false);
 * // Returns: true (slot is available and user has permission)
 */
export function isSlotAvailable(
  slot: { isAvailable: boolean; canBookPublic: boolean; canBookMember: boolean },
  isMember: boolean
): boolean {
  // Step 1: Check if slot is available at all
  // API sets isAvailable=false when:
  // - Slot is already booked
  // - Slot is in the past
  // - Slot is blocked/closed
  if (!slot.isAvailable) {
    return false;
  }
  
  // Step 2: Check membership-based permissions
  // canBookPublic/canBookMember indicate who has PERMISSION to book this type of slot
  // (e.g., member-only hours vs public hours)
  return isMember ? slot.canBookMember : slot.canBookPublic;
}

/**
 * Check whether a booking of `desiredDuration` minutes can start at `startSlot`.
 *
 * Returns `true` when the starting slot AND all consecutive `slotDuration`-min
 * slots required to cover the duration are available to the given user (respects
 * membership-based permissions). `slotDuration` is `startSlot.durationMinutes ?? 30`.
 *
 * Used by the UI to hide start times that can't fit the selected duration so
 * users don't see options they can't actually book.
 *
 * **In the aggregate (all-bays) grid this normally checks ONE slot.**
 * `BookingTimeStep` asks the API for slots whose length is the booking duration,
 * so `slotsNeeded` is 1 and the answer comes from the backend, which resolved it
 * per bay. That matters: club-wide availability is an OR across bays computed
 * per slot, so walking several shorter slots would accept a range stitched from
 * a different bay in each one — a booking no single bay can serve.
 *
 * **No current caller produces shorter cells.** `BookingTimeStep` sends
 * `durations` on every request, bay filter included, so `slotsNeeded` is always
 * 1 today and the walk below is a defensive path. It is kept so a caller that
 * legitimately holds interval cells still gets the right answer — do not make
 * it the aggregate grid's path again.
 *
 * @example
 * // Duration: 2h, slotDuration: 30 min (4 slots needed)
 * // Slots at 10:00, 10:30, 11:00 available; 11:30 booked
 * canFitDuration(slot1000, allSlots, 120, false); // false — can't fit last slotDuration
 */
export function canFitDuration(
  startSlot: TimeSlot,
  allSlots: TimeSlot[],
  desiredDuration: number,
  isMember: boolean,
): boolean {
  // Use the actual slot step size from the API (e.g. 30 or 60 min) so that
  // clubs configured with a 60-min interval don't fail the consecutive-slot
  // check by looking for a non-existent slot at startMinutes+30.
  const SLOT_DURATION = startSlot.durationMinutes ?? 30;
  const slotsNeeded = Math.ceil(desiredDuration / SLOT_DURATION);

  if (!isSlotAvailable(startSlot, isMember) || !isStartableRow(startSlot)) return false;
  if (slotsNeeded === 1) return true;

  let currentMinutes = startSlot.startMinutes + SLOT_DURATION;
  for (let i = 1; i < slotsNeeded; i++) {
    const nextSlot = allSlots.find((s) => s.startMinutes === currentMinutes);
    if (!nextSlot || !isSlotAvailable(nextSlot, isMember)) return false;
    currentMinutes += SLOT_DURATION;
  }
  return true;
}

/**
 * Build the consecutive-slot selection starting from a clicked slot.
 *
 * The grid filter (`canFitDuration`) already guarantees the clicked slot can
 * accommodate the full `desiredDuration`, so click-time is pure arithmetic on
 * `startMinutes` — no re-traversal or availability re-checks.
 *
 * Returns a single slot whenever the API slot already spans the whole duration
 * (the aggregate grid — see `canFitDuration`), in which case
 * `slot.startMinutes + slot.durationMinutes` is the booked end time.
 *
 * Prefers real API slots from `allSlots` for each position so downstream code
 * gets real metadata; falls back to a synthetic clone with the correct
 * `startMinutes` when a position isn't in the API list (edge case). That keeps
 * `slots[0].startMinutes` and `slots[last].startMinutes` — the only fields
 * `handleSlotsSelect` reads — always correct.
 */
export function getConsecutiveSlots(
  clickedSlot: TimeSlot,
  allSlots: TimeSlot[],
  desiredDuration: number,
): TimeSlot[] {
  // Use the actual slot step from the API slot, not a hardcoded 30.
  const SLOT_DURATION = clickedSlot.durationMinutes ?? 30;
  const slotsNeeded = Math.ceil(desiredDuration / SLOT_DURATION);

  return Array.from({ length: slotsNeeded }, (_, i) => {
    const minutes = clickedSlot.startMinutes + i * SLOT_DURATION;
    return (
      allSlots.find((s) => s.startMinutes === minutes) ?? {
        ...clickedSlot,
        startMinutes: minutes,
      }
    );
  });
}

/**
 * The grid cells a selection covers, as start-times-from-midnight.
 *
 * Drives which start buttons render selected. The chip and the screen-reader
 * announcement read the authoritative `SelectionRange` instead, so this only
 * has to say which cells to outline.
 *
 * `gridSlotLength` is the grid STEP (the slot interval), not the rendered slot
 * length: the highlight covers every interval-sized cell the booking spans, so
 * a 90-minute booking on a 30-minute interval outlines three cells.
 *
 * Times are minutes-from-midnight and may exceed 1440 on a club that runs past
 * midnight (`/docs/conventions/slot-times.md`); this only walks the range, and
 * leaves the end-of-day normalization to the callers that format it.
 */
export function buildSelectedSlots(
  startMinutes: number,
  endMinutes: number,
  gridSlotLength: number,
): number[] {
  // A non-positive length would spin forever — it arrives from response data.
  if (gridSlotLength <= 0) return [];

  const selected: number[] = [];
  for (let minutes = startMinutes; minutes < endMinutes; minutes += gridSlotLength) {
    selected.push(minutes);
  }
  return selected;
}

/**
 * Which durations the backend will actually serve from `startMinutes`.
 *
 * Reads the availability response, which carries one slot per (start, length) pair —
 * the backend resolved per-bay containment before OR-ing. A
 * length with no available slot at this start is one the club cannot serve.
 * Never chain shorter cells to approximate a longer one: that crosses the
 * per-bay OR.
 *
 * With no start chosen there is nothing to constrain, so every option stands.
 */
export function getAvailableDurations(
  startMinutes: number | null,
  probeSlots: TimeSlot[],
  durationOptions: number[],
  isMember: boolean,
): number[] {
  if (startMinutes === null || probeSlots.length === 0) return durationOptions;
  return durationOptions.filter((duration) =>
    probeSlots.some(
      (slot) =>
        slot.startMinutes === startMinutes &&
        slot.durationMinutes === duration &&
        isSlotAvailable(slot, isMember) &&
        isStartableRow(slot),
    ),
  );
}

/**
 * Announcement text for screen readers, built from the authoritative range.
 *
 * Deliberately does NOT report a slot count. It used to say "(90 minutes, 3 slots)",
 * where the count was `selectedSlots.length` — grid CELLS, a rendering detail the member
 * never chose, and one that disagrees with the screen whenever a covered cell is not a
 * valid start. The range is what they booked.
 */
export function getSelectionAnnouncement(range: SelectionRange | null): string {
  if (range === null) {
    return 'Time slot selection cleared';
  }

  const startTime = formatMinutesToTime(range.startMinutes);
  const endTime = formatSlotEndTime(range.endMinutes);
  // Raw values, before midnight normalization — a midnight-crossing booking
  // must not report a negative length.
  const duration = range.endMinutes - range.startMinutes;

  return `Time slot selected: ${startTime} to ${endTime} (${duration} minutes)`;
}
